/* eslint-disable @typescript-eslint/no-unused-vars */
import { ISbStoriesParams, StoryblokClient } from '@storyblok/react/rsc';

import { env } from '@natu/env';
import { tryCatch } from '@natu/utils/try-catch';

import {
  GetArticleAuthorItemsQueryVariables,
  GetArticleCategoryNodesQueryVariables,
  GetArticleNodesQueryVariables,
  GetConfigNodeQueryVariables,
  GetContentNodeQueryVariables,
  GetContentNodesQueryVariables,
  GetLinksQueryVariables,
} from './sdk.types';

export type SdkFunctionWrapper = <T>(
  action: (options?: ISbStoriesParams) => Promise<T>,
  operationName: string,
  operationType?: string,
) => Promise<T>;

const defaultWrapper: SdkFunctionWrapper = (action, _operationName, _operationType) => action();

export function getSdk(
  client: () => StoryblokClient,
  withWrapper: SdkFunctionWrapper = defaultWrapper,
) {
  return {
    getContentNode(variables: GetContentNodeQueryVariables) {
      const sbClient = client();
      const { slug, relations } = variables;

      return withWrapper(
        async wrapperOptions =>
          tryCatch(
            sbClient
              .get(`cdn/stories/${slug}`, {
                ...wrapperOptions,
                version: wrapperOptions?.version,
                resolve_relations: relations ?? wrapperOptions?.resolve_relations,
              })
              .then(res => res.data),
          ),
        'getContentNode',
        'GET',
      );
    },

    getContentNodes(variables: GetContentNodesQueryVariables) {
      const sbClient = client();
      const {
        relations,
        excludingSlugs,
        filterQuery,
        page,
        perPage,
        searchTerm,
        sortBy,
        startsWith,
        withTag,
        excludingFields,
        byUuids,
        byUuidsOrdered,
      } = variables;

      const DEFAULT_PAGE = 1;
      const DEFAULT_PER_PAGE = 12;

      return withWrapper(
        async wrapperOptions =>
          sbClient.get('cdn/stories', {
            ...wrapperOptions,
            page: page || DEFAULT_PAGE,
            per_page: perPage || DEFAULT_PER_PAGE,
            starts_with: startsWith ?? '',
            excluding_slugs: excludingSlugs ?? '',
            with_tag: withTag ?? '',
            search_term: searchTerm ?? '',
            filter_query: filterQuery ?? {},
            excluding_fields: excludingFields ?? '',
            sort_by: sortBy ?? '',
            version: wrapperOptions?.version,
            resolve_relations: relations ?? wrapperOptions?.resolve_relations,
            by_uuids: byUuids ?? '',
            by_uuids_ordered: byUuidsOrdered ?? '',
          }),
        'getContentNodes',
        'GET',
      );
    },

    getConfigNode(variables?: GetConfigNodeQueryVariables) {
      const sbClient = client();
      const { relations } = variables || {};

      return withWrapper(
        async wrapperOptions =>
          tryCatch(
            sbClient
              .get(`cdn/stories/${env.NEXT_PUBLIC_STORYBLOK_EXCLUDED_FOLDERS_FROM_ROUTING}`, {
                ...wrapperOptions,
                version: wrapperOptions?.version,
                resolve_relations: relations ?? wrapperOptions?.resolve_relations,
              })
              .then(res => res.data),
          ),
        'getConfigNode',
        'GET',
      );
    },

    getArticleAuthorItems(variables: GetArticleAuthorItemsQueryVariables) {
      const sbClient = client();
      const {
        excludingSlugs,
        filterQuery,
        page,
        perPage,
        relations,
        searchTerm,
        sortBy,
        startsWith,
        withTag,
        byUuids,
        excludingFields,
      } = variables || {};

      const DEFAULT_PAGE = 1;
      const DEFAULT_PER_PAGE = 12;

      return withWrapper(
        async wrapperOptions =>
          tryCatch(
            sbClient
              .get('cdn/stories', {
                ...wrapperOptions,
                page: page || DEFAULT_PAGE,
                per_page: perPage || DEFAULT_PER_PAGE,
                starts_with: startsWith ?? '',
                excluding_slugs: excludingSlugs ?? '',
                with_tag: withTag ?? '',
                search_term: searchTerm ?? '',
                by_uuids: byUuids ?? '',
                excluding_fields: excludingFields ?? '',
                filter_query: filterQuery ?? {
                  component: {
                    in: 'articleAuthor',
                  },
                },
                sort_by: sortBy ?? '',
                version: wrapperOptions?.version,
                resolve_relations: relations ?? wrapperOptions?.resolve_relations,
              })
              .then(res => res.data),
          ),
        'getArticleAuthorItems',
        'GET',
      );
    },

    getArticleCategoryNodes(variables: GetArticleCategoryNodesQueryVariables) {
      const sbClient = client();
      const {
        excludingSlugs,
        filterQuery,
        page,
        perPage,
        relations,
        searchTerm,
        sortBy,
        startsWith,
        withTag,
        excludingFields,
        byUuids,
        byUuidsOrdered,
      } = variables || {};

      const DEFAULT_PAGE = 1;
      const DEFAULT_PER_PAGE = 12;

      return withWrapper(
        async wrapperOptions =>
          tryCatch(
            sbClient
              .get('cdn/stories', {
                ...wrapperOptions,
                page: page || DEFAULT_PAGE,
                per_page: perPage || DEFAULT_PER_PAGE,
                starts_with: startsWith ?? '',
                excluding_slugs: excludingSlugs ?? '',
                with_tag: withTag ?? '',
                search_term: searchTerm ?? '',
                excluding_fields: excludingFields ?? '',
                filter_query: filterQuery ?? {
                  component: {
                    in: 'articleCategory',
                  },
                },
                by_uuids_ordered: byUuidsOrdered ?? '',
                by_uuids: byUuids ?? '',
                sort_by: sortBy ?? '',
                version: wrapperOptions?.version,
                resolve_relations: relations ?? wrapperOptions?.resolve_relations,
              })
              .then(res => res.data),
          ),
        'getArticleCategoryNodes',
        'GET',
      );
    },

    getArticleNodes(variables?: GetArticleNodesQueryVariables) {
      const sbClient = client();
      const {
        excludingSlugs,
        filterQuery,
        page,
        perPage,
        relations,
        searchTerm,
        sortBy,
        startsWith,
        withTag,
        excludingFields,
        byUuidsOrdered,
      } = variables || {};

      const DEFAULT_PAGE = 1;
      const DEFAULT_PER_PAGE = 12;

      return withWrapper(
        async wrapperOptions =>
          sbClient.get('cdn/stories', {
            ...wrapperOptions,
            page: page || DEFAULT_PAGE,
            per_page: perPage || DEFAULT_PER_PAGE,
            starts_with: startsWith ?? '',
            excluding_slugs: excludingSlugs ?? '',
            with_tag: withTag ?? '',
            by_uuids_ordered: byUuidsOrdered ?? '',
            search_term: searchTerm ?? '',
            excluding_fields: excludingFields ?? '',
            filter_query: filterQuery ?? {
              component: {
                in: 'article',
              },
            },
            sort_by: sortBy ?? '',
            version: wrapperOptions?.version,
            resolve_relations: relations ?? wrapperOptions?.resolve_relations,
          }),
        'getArticleNodes',
        'GET',
      );
    },

    getLinks(variables?: GetLinksQueryVariables) {
      const sbClient = client();
      const { startsWith, perPage, page } = variables || {};

      return withWrapper(
        async wrapperOptions =>
          tryCatch(
            sbClient
              .get('cdn/links', {
                ...wrapperOptions,
                starts_with: startsWith ?? '',
                per_page: perPage ?? 1000,
                page: page ?? 1,
              })
              .then(res => res.data),
          ),
        'getLinks',
        'GET',
      );
    },
  };
}

export type Sdk = ReturnType<typeof getSdk>;
